import React, { useState, useEffect } from 'react';
import { X, Mail, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const OTPModal = ({
  isOpen,
  email,
  onClose,
  onVerify,
  isLoading = false,
  error = null,
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Timer for OTP expiration
  useEffect(() => {
    if (!isOpen || verificationSuccess) return;

    if (timeLeft <= 0) {
      setLocalError('OTP expired. Please request a new one.');
      return;
    }

    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, isOpen, verificationSuccess]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '', '', '']);
      setTimeLeft(60);
      setVerificationSuccess(false);
      setLocalError(error || null);
      setIsResending(false);
    }
  }, [isOpen, error]);

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only keep last digit
    setOtp(newOtp);
    setLocalError(null);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle OTP verification
  const handleVerify = async () => {
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setLocalError('Please enter all 6 digits');
      return;
    }

    if (isLoading) return;

    // Call parent verification function
    const isValid = await onVerify(otpString);

    if (isValid) {
      setVerificationSuccess(true);
      // Close modal after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } else {
      setLocalError('Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (isResending || timeLeft > 0) return;

    setIsResending(true);
    setLocalError(null);

    // Simulate API call
    setTimeout(() => {
      setTimeLeft(60);
      setOtp(['', '', '', '', '', '']);
      setIsResending(false);
      // Focus first input
      const firstInput = document.getElementById('otp-0');
      if (firstInput) firstInput.focus();
    }, 1000);
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData.length >= 6) {
      const newOtp = pastedData.slice(0, 6).split('');
      setOtp(newOtp);
      setLocalError(null);
      // Focus last input
      const lastInput = document.getElementById('otp-5');
      if (lastInput) lastInput.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      {/* Modal Container */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <X size={24} className="text-gray-600" />
        </button>

        {verificationSuccess ? (
          // Success Screen
          <div className="p-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center animate-bounce" style={{ backgroundColor: '#10b981' }}>
                <CheckCircle size={32} className="text-white" />
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Montserrat' }}>
                Email Verified!
              </h2>
              <p className="text-gray-600">
                Your email has been successfully verified.
              </p>
            </div>

            <p className="text-sm text-gray-500">
              Redirecting to complete registration...
            </p>
          </div>
        ) : (
          // OTP Input Screen
          <div className="p-8 space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#0095fa', opacity: 0.2 }}>
                  <Mail size={24} style={{ color: '#0095fa' }} />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Montserrat' }}>
                Verify Email
              </h2>
              <p className="text-gray-600 text-sm">
                We've sent a 6-digit OTP to
              </p>
              <p className="text-gray-800 font-semibold">{email}</p>
            </div>

            {/* OTP Input Fields */}
            <div className="space-y-4">
              <div
                className="flex justify-center gap-3"
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isLoading || verificationSuccess}
                    placeholder="0"
                    className="w-12 h-12 text-center text-xl font-bold rounded-lg border-2 transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    style={{
                      borderColor: digit ? '#0095fa' : '#e5e7eb',
                      backgroundColor: digit ? '#f0f4ff' : '#ffffff',
                      color: '#0095fa',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#0095fa';
                      e.target.style.boxShadow = '0 0 0 3px rgba(0, 149, 250, 0.1)';
                    }}
                    onBlur={(e) => {
                      if (!e.target.value) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                ))}
              </div>

              {/* Error Message */}
              {localError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{localError}</p>
                </div>
              )}

              {/* Timer and Resend */}
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">
                  {timeLeft > 0 ? (
                    <>
                      Resend OTP in{' '}
                      <span className="font-bold text-gray-800">
                        {timeLeft}s
                      </span>
                    </>
                  ) : (
                    <button
                      onClick={handleResendOtp}
                      disabled={isResending}
                      className="text-blue-600 hover:text-blue-700 font-semibold transition-colors disabled:opacity-50"
                    >
                      {isResending ? 'Resending...' : 'Resend OTP'}
                    </button>
                  )}
                </p>

                {/* Resend Help Text */}
                {timeLeft === 0 && !isResending && (
                  <p className="text-xs text-gray-500">
                    Didn't receive the code? Check spam folder
                  </p>
                )}
              </div>
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerify}
              disabled={isLoading || otp.some((digit) => !digit)}
              className="w-full py-3 rounded-lg font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#0095fa',
                boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
              }}
              onMouseEnter={(e) => {
                if (!isLoading && !otp.some((digit) => !digit)) {
                  e.target.style.backgroundColor = '#0f6caa';
                  e.target.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#0095fa';
                e.target.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Email
                  <CheckCircle size={18} />
                </>
              )}
            </button>

            {/* Help Text */}
            <p className="text-center text-xs text-gray-500">
              Didn't receive an email?{' '}
              <button
                onClick={onClose}
                className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              >
                Use different email
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OTPModal;